import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { translate } from '@data/translations';
import { Reservation } from '../resController';
import {
    ReservationChangeLogModel,
    AccommodationPricelistModel,
    ReservationSource,
    TravelPurposeCode,
    BOARD_LABELS,
    BoardType,
    TaxModel,
} from '@common/modelDefinition';
import {
    InvoicedStatusEnumText,
    InvoiceReclamationStatusEnums,
    InvoiceTypeEnums,
    TypeOfPaymentEnums,
} from '@common/enums';
import { getReservationChangeLog, getCustomer, getCompany, getUserInfo, getReservation } from '../api';
import { reservationStatusEnumsDesc } from '@data/reservationStatusEnums';
import MiniLoader from '@components/loaders/miniLoader';
import useStore from '@data/state/zustand';
import { getReservationTotalPrice, DefaultPosItem } from '../utils';

interface ReservationHistoryProps {
    reservation: Reservation;
    history: any;
}

const FIELD_LABELS: Record<string, string> = {
    adultsNumber: 'Adults',
    childrenNumber: 'Children',
    checkInTimestamp: 'Check in',
    checkOutTimestamp: 'Check out',
    roomInfoId: 'Room',
    statusEnum: 'Status',
    notes: 'Notes',
    statusChangeNotes: 'Notes',
    discountPercentage: 'Discount',
    board: 'Board',
    groupUuid: 'Group',
    previousGroupUuid: 'resLog_PreviousGroup',
    groupName: 'Group name',
    tags: 'Tags',
    customerId: 'Holder',
    companyId: 'Company',
    receptionistUuid: 'Made by',
    condoUserUuid: 'Condo user',
    enableSelfCheckIn: 'Enable self check-in',
    providerReservation: 'resLog_ChannelManagerReservation',
    invoicedStatus: 'Payment status',
    canceledReservationInvoicingStatus: 'Invoicing method',
    reservationSource: 'resLog_ReservationSource',
    channelName: 'Channel',
    travelPurposeCode: 'TravelPurpose',
    cardCode: 'resLog_CardCode',
    cardCodeValidFrom: 'resLog_CardCodeValidFrom',
    cardCodeValidTo: 'resLog_CardCodeValidTo',
    type: 'resLog_CardType',
    guest1CodeCopy: 'resLog_CardCode',
    guest2CodeCopy: 'resLog_CardCode',
    guest3CodeCopy: 'resLog_CardCode',
    invoiceNumberFull: 'Invoice',
    totalValue: 'Price',
    invoiceType: 'Invoice type',
    reclamation: 'Status',
    typeOfPayment: 'Payment type',
    paymentAmount: 'Amount',
    reclamationReason: 'resLog_StornoReason',
    note: 'Note',
    reservationId: 'Reservation',
    documentType: 'Document type',
    documentId: 'Document id',
    documentValidityPeriod: 'Document Validity Period',
    visaTypeAndNumber: 'Visa number',
    visaValidityPeriod: 'Visa Validity Period',
    visaEntryDate: 'Date entry in country',
    placeEntry: 'Entry place',
    visaIssuancePlace: 'Visa issuance place',
    residencePermitExpiryDate: 'Right to reside (in the country)',
    visaTypeCode: 'Visa type',
    identityCardIssuer: 'Issuing authority of travel document',
    paymentCategory: 'resLog_PaymentCategory',
    residenceTaxReductionCode: 'residenceTaxReductionCodeCisu',
};

const ACTION_TYPE_LABELS: Record<string, string> = {
    CREATE: 'Created',
    UPDATE: 'resLog_Updated',
    CANCEL: 'Canceled',
    CHECK_IN: 'Checked in',
    CHECK_OUT: 'Closed',
    CONFIRM: 'Confirmed',
    AUTO_BLOCK: 'Auto blocked',
    STATUS_CHANGE: 'resLog_StatusChanged',
    ROOM_CHANGE: 'resLog_RoomChanged',
    DATE_CHANGE: 'resLog_DateChanged',
    GUEST_COUNT_CHANGE: 'resLog_GuestCountChanged',
    TAX_ASSIGNED: 'resLog_TaxAssigned',
    CARD_CODE_ASSIGNED: 'resLog_CardCodeAssigned',
    INVOICE_ISSUE: 'resLog_InvoiceIssued',
    INVOICE_STORNO: 'resLog_InvoiceStorno',
    INVOICE_ATTACHED: 'resLog_InvoiceAttached',
    INVOICE_DETACHED: 'resLog_InvoiceRemoved',
    GUEST_REMOVED: 'resLog_GuestRemoved',
    MINIBAR_CONSUMPTION: 'Minibar consumption',
    MINIBAR_CONSUMPTION_REMOVED: 'resLog_MinibarConsumptionRemoved',
};

const CHANGED_BY_SYSTEM_LABELS: Record<string, string> = {
    CHM_WUBOOK: 'resLog_ChannelManagerSync',
    ONLINE_RECEPTION: 'resLog_OnlineReception',
    SELF_CHECKIN: 'Self check in',
    HARDWARE_SRC: 'resLog_Hardware',
    SCHEDULED_JOB: 'resLog_ScheduledJob',
    EXTERNAL_API: 'resLog_ExternalApi',
    UNKNOWN: 'Unknown',
};

// 0/1/2 = Puna cijena/Parcijalna uplata/Potencijalno nista - iste labele kao dropdown u mainReservationInfo.tsx
const CANCELED_INVOICING_STATUS_LABELS: Record<number, string> = {
    0: 'Full price',
    1: 'Partial fee',
    2: 'No charge',
};

// BMSSrcCardCode.type - kome kartica pripada (guest/maid/manager), NE isto sto i cardCodeTypes hw protokol enum
const CARD_TYPE_LABELS: Record<string, string> = {
    guest: 'guest',
    maid: 'maid',
    manager: 'manager',
};

// ReservationGuestReference.documentType - isti kodovi/labele kao dropdown u visaDataModal.tsx
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
    P: 'Passport',
    I: 'Identity card',
    V: 'Visa',
    D: 'Driving licence',
    X: 'Other',
};

// ReservationGuestReference.visaTypeCode - isto kao dropdown u visaDataModal.tsx (K=kratkorocno, D=dugorocno)
const VISA_TYPE_CODE_LABELS: Record<string, string> = {
    K: 'resLog_ShortTerm',
    D: 'resLog_LongTerm',
};

// ReservationGuestReference.residenceTaxReductionCode (CISU) - ista lista kao RESIDENCE_TAX_REDUCTION_LABELS
// u pages/cisu/CisuDashboard.tsx (ResidenceTaxReductionCodeCISU enum, modelDefinition.ts)
const RESIDENCE_TAX_REDUCTION_LABELS: Record<number, string> = {
    1: 'Children under 12',
    2: 'Spa/climate treatment - referral',
    3: 'Spa/climate treatment - individual',
    4: 'Persons with disabilities',
    5: 'Children with developmental disabilities - foreign care',
    6: 'Adults with disabilities - foreign care',
    7: 'Students - organized stay',
    8: 'Students - schooling',
    9: 'Foreign nationals tax exempt',
    10: 'Youth 12-18',
    11: 'Stay longer than 30 days',
};

const DATE_FIELDS = ['checkInTimestamp', 'checkOutTimestamp', 'cardCodeValidFrom', 'cardCodeValidTo'];
// ReservationGuestReference datumska polja koja se spremaju kao string (npr. "2026-07-15"), ne kao
// epoch-ms BIGINT poput DATE_FIELDS iznad - prikazuju se preko formatDateOnly, bez vremena.
const DATE_STRING_FIELDS = [
    'documentValidityPeriod',
    'visaValidityPeriod',
    'visaEntryDate',
    'residencePermitExpiryDate',
];
const BOOLEAN_FIELDS = ['enableSelfCheckIn', 'providerReservation'];
const PRICE_RELATED_FIELDS = ['accommodationPriceListId', 'openPrice', 'rememberedAccommodationPriceInfo'];
// fixedTaxId/fixedTaxName1/fixedTaxAmount1 (boravisna pristojba) i fixedTaxId2/fixedTaxName2/
// fixedTaxAmount2 (osiguranje) zajedno opisuju JEDNU dodijeljenu stavku - spajaju se u buildTaxDisplayRow
// pa se ovdje samo izbacuju iz "ostalih" redova da se ne prikazu triput (kao id, kao ime, kao iznos).
const TAX_RELATED_FIELDS = ['fixedTaxId', 'fixedTaxName1', 'fixedTaxAmount1'];
const INSURANCE_RELATED_FIELDS = ['fixedTaxId2', 'fixedTaxName2', 'fixedTaxAmount2'];

// Customer/Company/User podaci se ne drze globalno u store-u (za razliku od AccommodationPriceList/
// RoomInfo), pa se imena dohvacaju on-demand za ID/UUID vrijednosti koje se pojave u logovima
// (vidi loadCustomerNames/loadCompanyNames/loadUserNames).
function resolveFromCache(value: string | null, cache: Record<string, string>): string {
    if (value === null || value === undefined) {
        return '-';
    }
    return cache[value] || value;
}

function resolvePriceListName(value: string | null): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    const priceLists: AccommodationPricelistModel[] = useStore.getState().model.AccommodationPriceList;
    const found = priceLists?.find((p) => String(p.id) === String(value));
    return found?.name || value;
}

// Isti obrazac kao TableContextMenuWraper.tsx ('datetime' kolone) - postuje otherSettings.dateFormat
// (postavke -> Jezik i valuta), s HH:mm uvijek fiksno dodanim na kraj (24-satni format, bez AM/PM).
function formatDateTime(timestamp: number): string {
    const otherSettings = useStore.getState().otherSettings;
    const format = otherSettings?.dateFormat ? otherSettings.dateFormat + ' HH:mm:ss' : 'DD.MM.YYYY. HH:mm:ss';
    return dayjs(timestamp).format(format);
}

// Za DATE_STRING_FIELDS (npr. visaValidityPeriod) - polje je slobodan string, ne pouzdan epoch-ms
// (kod bulk importa moze ostati sirovi tekst iz izvorne datoteke ako parsiranje ne uspije), pa se
// prikazuje formatirano SAMO ako je stvarno valjan datum, inace se vraca sirova vrijednost.
function formatDateOnly(value: string): string {
    const otherSettings = useStore.getState().otherSettings;
    const parsed = dayjs(value);
    if (!parsed.isValid()) {
        return value;
    }
    return parsed.format(otherSettings?.dateFormat || 'DD.MM.YYYY.');
}

function resolveRoomName(value: string | null): string {
    if (value === null || value === undefined) {
        return '-';
    }
    const rooms: any[] = useStore.getState().model.RoomInfo;
    const found = rooms?.find((r) => String(r.id) === String(value));
    return found?.name || value;
}

// fixedTaxId/fixedTaxId2 mogu referencirati ili Tax ili PosItem zapis - log ne cuva fixedTaxType
// (namjerno izbaceno iz diffa, previse suma), pa se ovdje ID pokusava razrijesiti kroz oba izvora.
function resolveTaxItemInfo(value: string | null): { name: string; amount: number | null } | null {
    if (value === null || value === undefined) {
        return null;
    }
    const numericId = Number(value);
    if (isNaN(numericId) || numericId <= 0) {
        return null;
    }
    const taxes: TaxModel[] = useStore.getState().model.Tax || [];
    const tax = taxes.find((t) => Number(t.id) === numericId);
    if (tax) {
        return { name: tax.name, amount: tax.fixedAmount ? Number(tax.fixedAmount) : null };
    }
    const defaultPosItems: DefaultPosItem[] = useStore.getState().invoicingSettings?.defaultPosItems || [];
    const posItem = defaultPosItems.find((p) => Number(p.posItemId) === numericId);
    if (posItem) {
        return { name: posItem.posItemName, amount: posItem.posItemPrice ? Number(posItem.posItemPrice) : null };
    }
    return null;
}

function resolveOpenPrice(value: string | null): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    const numeric = Number(value);
    if (isNaN(numeric) || numeric <= 0) {
        return null;
    }
    return numeric.toFixed(2);
}

// rememberedAccommodationPriceInfo je append-only povijesni niz - svaki preracun cijene GURA
// novi element na kraj, stari ostaju. Relevantna/trenutna cijena je uvijek ZADNJI element niza.
// Zadnji element cesto nema `totalPrice` (stariji format, samo priceOfferIntervals/apRules), pa
// se za izracun ponovno koristi ista fallback logika kao u getReservationTotalPrice (utils.ts) -
// prosljedujemo joj bas taj zadnji offer kao "trenutnu rezervaciju" (koristi njegov vlastiti
// adultsNumber/board za obracun, ne live stanje rezervacije).
function resolveRememberedPrice(value: string | null): string | null {
    if (!value) {
        return null;
    }
    try {
        const offers = JSON.parse(value);
        if (!Array.isArray(offers) || offers.length === 0) {
            return null;
        }
        const lastOffer = offers[offers.length - 1];
        const total = getReservationTotalPrice({
            openPrice: lastOffer?.openPrice ?? null,
            rememberedAccommodationPriceInfo: offers,
            adultsNumber: lastOffer?.adultsNumber,
            board: lastOffer?.board,
        } as any);
        if (!total) {
            return null;
        }
        return Number(total).toFixed(2);
    } catch {
        return null;
    }
}

interface NameCaches {
    customerNames: Record<string, string>;
    companyNames: Record<string, string>;
    userNames: Record<string, string>;
    reservationLabels: Record<string, string>;
}

function formatFieldValue(
    fieldName: string,
    value: string | null,
    entityType: string,
    caches: NameCaches
): string {
    // residenceTaxReductionCode nema odvojeno "prazno/nepoznato" stanje - null je eksplicitno
    // "Bez olaksice" (jedina takva opcija u dropdownu, visaDataModal.tsx), pa se mora obraditi
    // PRIJE generickog null->'-' pravila, inace se gubi razlika prema stvarno neupisanom polju.
    if (fieldName === 'residenceTaxReductionCode' && (value === null || value === undefined)) {
        return translate('No reduction');
    }
    if (value === null || value === undefined) {
        return '-';
    }
    if (fieldName === 'statusEnum') {
        if (entityType === 'BMSSrcCardCode') {
            return value === '1' ? translate('resLog_Programmed') : translate('Pending');
        }
        const desc = (reservationStatusEnumsDesc as Record<number, string>)[Number(value)];
        return desc || value;
    }
    if (fieldName === 'type' && entityType === 'BMSSrcCardCode') {
        return translate(CARD_TYPE_LABELS[value] || value);
    }
    if (fieldName === 'customerId') {
        return resolveFromCache(value, caches.customerNames);
    }
    if (fieldName === 'companyId') {
        return resolveFromCache(value, caches.companyNames);
    }
    if (fieldName === 'receptionistUuid' || fieldName === 'condoUserUuid') {
        return resolveFromCache(value, caches.userNames);
    }
    if (fieldName === 'roomInfoId') {
        return resolveRoomName(value);
    }
    if (fieldName === 'reservationId' && entityType === 'Invoice') {
        return resolveFromCache(value, caches.reservationLabels);
    }
    if (BOOLEAN_FIELDS.includes(fieldName)) {
        return value === '1' || value === 'true' ? translate('Yes') : translate('No');
    }
    if (fieldName === 'invoicedStatus') {
        return translate((InvoicedStatusEnumText as any)[Number(value)]) || value;
    }
    if (fieldName === 'canceledReservationInvoicingStatus') {
        return translate(CANCELED_INVOICING_STATUS_LABELS[Number(value)]) || value;
    }
    if (fieldName === 'reservationSource') {
        return translate((ReservationSource as any)[Number(value)]) || value;
    }
    if (fieldName === 'travelPurposeCode') {
        return translate((TravelPurposeCode as any)[Number(value)]) || value;
    }
    if (fieldName === 'invoiceType') {
        return translate((InvoiceTypeEnums as any)[Number(value)]) || value;
    }
    if (fieldName === 'reclamation') {
        return translate((InvoiceReclamationStatusEnums as any)[Number(value)]) || value;
    }
    if (fieldName === 'typeOfPayment') {
        return translate((TypeOfPaymentEnums as any)[value] || (value === 'O' ? 'Other' : value));
    }
    if (fieldName === 'board') {
        return translate(BOARD_LABELS[value as BoardType]) || value;
    }
    if (fieldName === 'documentType') {
        return translate(DOCUMENT_TYPE_LABELS[value] || value);
    }
    if (fieldName === 'visaTypeCode') {
        return translate(VISA_TYPE_CODE_LABELS[value] || value);
    }
    if (fieldName === 'residenceTaxReductionCode') {
        return translate(RESIDENCE_TAX_REDUCTION_LABELS[Number(value)]) || value;
    }
    if (DATE_FIELDS.includes(fieldName)) {
        const numericValue = Number(value);
        if (!isNaN(numericValue) && numericValue > 0) {
            return formatDateTime(numericValue);
        }
    }
    if (DATE_STRING_FIELDS.includes(fieldName)) {
        return formatDateOnly(value);
    }
    // Opca sigurnosna mjera - ako se ikad pojavi neocekivano dugacko sirovo polje (npr. buduci
    // legacy/import podaci), ne razvuci tablicu - prikazi skraceno umjesto sirovog bloka teksta.
    if (value.length > 150) {
        return value.slice(0, 150) + '…';
    }
    return value;
}

interface DisplayRow {
    key: string;
    recordedAt: number;
    actionLabel: string;
    actorLabel: string;
    label: string;
    oldDisplay: string;
    newDisplay: string;
}

// Cjenik (accommodationPriceListId), rucna cijena (openPrice) i izracunata ukupna cijena
// (rememberedAccommodationPriceInfo) zajedno opisuju JEDNU stvar - "koja je cijena smjestaja" -
// pa se spajaju u jedan red umjesto tri razdvojena. Prioritet po strani (staro/novo) je: naziv
// cjenika ako je odabran > rucna cijena > izracunata ukupna cijena - tako "prijelaz s cjenika na
// rucnu cijenu" (ili obratno) ispadne kao npr. "Apartman STUDIO -> 500.00", a ne kao dva
// nepovezana retka.
function buildPriceDisplayRow(group: ReservationChangeLogModel[]): { oldDisplay: string; newDisplay: string } | null {
    const priceListRow = group.find((r) => r.fieldName === 'accommodationPriceListId');
    const openPriceRow = group.find((r) => r.fieldName === 'openPrice');
    const rememberedRow = group.find((r) => r.fieldName === 'rememberedAccommodationPriceInfo');

    if (!priceListRow && !openPriceRow && !rememberedRow) {
        return null;
    }

    const resolveSide = (side: 'oldValue' | 'newValue'): string => {
        const priceListName = priceListRow ? resolvePriceListName(priceListRow[side]) : null;
        if (priceListName) {
            return priceListName;
        }
        const openPrice = openPriceRow ? resolveOpenPrice(openPriceRow[side]) : null;
        if (openPrice) {
            return openPrice;
        }
        const rememberedPrice = rememberedRow ? resolveRememberedPrice(rememberedRow[side]) : null;
        if (rememberedPrice) {
            return rememberedPrice;
        }
        return '-';
    };

    return { oldDisplay: resolveSide('oldValue'), newDisplay: resolveSide('newValue') };
}

// fixedTaxId/fixedTaxName1/fixedTaxAmount1 (i isto za _2/osiguranje) opisuju JEDNU dodijeljenu
// stavku (naziv + iznos) - spajaju se u jedan red umjesto tri (sirovi id, ime, iznos zasebno).
// Prikazuje se citljivo ime (fixedTaxName1/2), s iznosom u zagradi ako je poznat i > 0; sirovi
// id se koristi samo ako ime iz nekog razloga nije poslano.
function buildTaxDisplayRow(
    group: ReservationChangeLogModel[],
    idField: string,
    nameField: string,
    amountField: string
): { oldDisplay: string; newDisplay: string } | null {
    const idRow = group.find((r) => r.fieldName === idField);
    const nameRow = group.find((r) => r.fieldName === nameField);
    const amountRow = group.find((r) => r.fieldName === amountField);

    if (!idRow && !nameRow && !amountRow) {
        return null;
    }

    const resolveSide = (side: 'oldValue' | 'newValue'): string => {
        const name = nameRow ? nameRow[side] : null;
        const rawId = idRow ? idRow[side] : null;
        const rawAmount = amountRow ? amountRow[side] : null;
        let label = name;
        let amount = rawAmount !== null && rawAmount !== undefined ? Number(rawAmount) : NaN;

        if (!label && rawId === '0') {
            label = translate('No charge');
        } else if (!label && rawId) {
            // fixedTaxName1/2 nije zabiljezen u ovoj akciji (npr. gost tek kreiran, samo ID
            // poslan) - pokusaj razrijesiti ime i iznos iz trenutno ucitanih Tax/PosItem podataka.
            const resolved = resolveTaxItemInfo(rawId);
            if (resolved) {
                label = resolved.name;
                if (isNaN(amount) && resolved.amount) {
                    amount = resolved.amount;
                }
            }
        }

        label = label || rawId;
        if (label === null || label === undefined) {
            return '-';
        }
        if (!isNaN(amount) && amount > 0) {
            return `${label} (${amount.toFixed(2)})`;
        }
        return label;
    };

    return { oldDisplay: resolveSide('oldValue'), newDisplay: resolveSide('newValue') };
}

function groupByActionUuid(logs: ReservationChangeLogModel[]): ReservationChangeLogModel[][] {
    const groups: ReservationChangeLogModel[][] = [];
    let currentGroup: ReservationChangeLogModel[] = [];
    let currentActionUuid: string | null = null;
    for (const log of logs) {
        if (log.actionUuid !== currentActionUuid) {
            if (currentGroup.length > 0) {
                groups.push(currentGroup);
            }
            currentGroup = [];
            currentActionUuid = log.actionUuid;
        }
        currentGroup.push(log);
    }
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }
    return groups;
}

// Mapira ReservationGuestReference.id (entityId) -> customerId, skenirajuci SVE loge za customerId
// diff na tom entitetu (CREATE/DELETE uvijek ima customerId red - vidi "Gost" label nize). Koristi
// se da se uz boravisnu/osiguranje/vizu/dokumente tog gosta ispise i njegovo ime - inace nejasno
// kojem gostu ti retci pripadaju kad na rezervaciji ima vise od jednog gosta.
function buildEntityIdToCustomerIdMap(logs: ReservationChangeLogModel[]): Record<number, string> {
    const map: Record<number, string> = {};
    logs.forEach((row) => {
        if (
            row.entityType === 'ReservationGuestReference' &&
            row.fieldName === 'customerId' &&
            row.entityId !== null &&
            row.entityId !== undefined
        ) {
            const customerId = row.newValue ?? row.oldValue;
            if (customerId) {
                map[row.entityId] = customerId;
            }
        }
    });
    return map;
}

function resolveGuestNameForEntity(
    entityId: number | null | undefined,
    entityIdToCustomerId: Record<number, string>,
    customerNames: Record<string, string>
): string | null {
    if (entityId === null || entityId === undefined) {
        return null;
    }
    const customerId = entityIdToCustomerId[entityId];
    if (!customerId) {
        return null;
    }
    return customerNames[customerId] || null;
}

function buildDisplayRows(
    groups: ReservationChangeLogModel[][],
    caches: NameCaches,
    entityIdToCustomerId: Record<number, string>
): DisplayRow[] {
    const rows: DisplayRow[] = [];
    groups.forEach((group, groupIndex) => {
        const first = group[0];
        const actionLabel = translate(ACTION_TYPE_LABELS[first.actionType] || first.actionType);
        const actorLabel = first.ChangedByUser
            ? `${first.ChangedByUser.name} ${first.ChangedByUser.surname}`
            : first.changedBySystem
            ? translate(CHANGED_BY_SYSTEM_LABELS[first.changedBySystem] || first.changedBySystem)
            : translate('Unknown');

        const otherRows = group.filter(
            (row) =>
                !PRICE_RELATED_FIELDS.includes(row.fieldName) &&
                !TAX_RELATED_FIELDS.includes(row.fieldName) &&
                !INSURANCE_RELATED_FIELDS.includes(row.fieldName)
        );
        otherRows.forEach((row) => {
            // customerId ima drugaciji smisao ovisno o entitetu: na Reservation je to nositelj,
            // na ReservationGuestReference je to samo "koji gost" (moze biti i suputnik, ne nuzno nositelj)
            // invoiceNumberFull koristi labelu same akcije (Invoice issued/Invoice storno) umjesto
            // generickog "Invoice" - jasnije govori sto se dogodilo, ne samo koji je broj racuna
            // MinibarLog isto prefiksira labelu akcijom (Minibar consumption/Minibar consumption removed)
            // ispred imena artikla - actionLabel vec sam po sebi razlikuje dodano/uklonjeno, pa se ne
            // dodaje jos jedan "Uklonjeno" segment - bio bi redundantan
            const label =
                row.fieldName === 'customerId' && row.entityType === 'ReservationGuestReference'
                    ? translate('Guest')
                    : row.fieldName === 'invoiceNumberFull'
                    ? actionLabel
                    : row.entityType === 'MinibarLog'
                    ? `${actionLabel} - ${translate(FIELD_LABELS[row.fieldName] || row.fieldName)}`
                    : translate(FIELD_LABELS[row.fieldName] || row.fieldName);
            // "Gost" red vec SAM PO SEBI pokazuje ime (kao vrijednost) - ne treba mu dupli suffix
            const guestName =
                row.entityType === 'ReservationGuestReference' && row.fieldName !== 'customerId'
                    ? resolveGuestNameForEntity(row.entityId, entityIdToCustomerId, caches.customerNames)
                    : null;
            rows.push({
                key: `${groupIndex}-${row.id}`,
                recordedAt: first.recordedAt,
                actionLabel,
                actorLabel,
                label: guestName ? `${label} (${guestName})` : label,
                oldDisplay: formatFieldValue(row.fieldName, row.oldValue, row.entityType, caches),
                newDisplay: formatFieldValue(row.fieldName, row.newValue, row.entityType, caches),
            });
        });

        const priceRow = buildPriceDisplayRow(group);
        if (priceRow && priceRow.oldDisplay !== priceRow.newDisplay) {
            rows.push({
                key: `${groupIndex}-price`,
                recordedAt: first.recordedAt,
                actionLabel,
                actorLabel,
                label: translate('Price'),
                oldDisplay: priceRow.oldDisplay,
                newDisplay: priceRow.newDisplay,
            });
        }

        // fixedTaxId/fixedTaxId2 (i pripadni naziv/iznos) mogu pripadati RAZLICITIM gostima unutar
        // iste akcije (npr. jedan Save koji mijenja poreze za dva gosta odjednom dijeli isti
        // actionUuid) - grupiraj po entityId prije spajanja u jedan red, inace bi se dva gosta
        // pomijesala u isti "Boravisna pristojba"/"Osiguranje" redak.
        const guestReferenceEntityIds = Array.from(
            new Set(
                group
                    .filter(
                        (row) =>
                            row.entityType === 'ReservationGuestReference' &&
                            (TAX_RELATED_FIELDS.includes(row.fieldName) || INSURANCE_RELATED_FIELDS.includes(row.fieldName))
                    )
                    .map((row) => row.entityId)
                    .filter((entityId): entityId is number => entityId !== null && entityId !== undefined)
            )
        );

        guestReferenceEntityIds.forEach((entityId) => {
            const entityGroup = group.filter((row) => row.entityId === entityId);
            const guestName = resolveGuestNameForEntity(entityId, entityIdToCustomerId, caches.customerNames);
            const guestSuffix = guestName ? ` (${guestName})` : '';

            const taxRow = buildTaxDisplayRow(entityGroup, 'fixedTaxId', 'fixedTaxName1', 'fixedTaxAmount1');
            if (taxRow && taxRow.oldDisplay !== taxRow.newDisplay) {
                rows.push({
                    key: `${groupIndex}-tax-${entityId}`,
                    recordedAt: first.recordedAt,
                    actionLabel,
                    actorLabel,
                    label: translate('Accommodation tax') + guestSuffix,
                    oldDisplay: taxRow.oldDisplay,
                    newDisplay: taxRow.newDisplay,
                });
            }

            const insuranceRow = buildTaxDisplayRow(entityGroup, 'fixedTaxId2', 'fixedTaxName2', 'fixedTaxAmount2');
            if (insuranceRow && insuranceRow.oldDisplay !== insuranceRow.newDisplay) {
                rows.push({
                    key: `${groupIndex}-insurance-${entityId}`,
                    recordedAt: first.recordedAt,
                    actionLabel,
                    actorLabel,
                    label: translate('Insurance') + guestSuffix,
                    oldDisplay: insuranceRow.oldDisplay,
                    newDisplay: insuranceRow.newDisplay,
                });
            }
        });
    });
    return rows;
}

// Skuplja jedinstvene vrijednosti za dano polje (iz oldValue/newValue) preko svih logova - koristi
// se za batch-dohvat imena (customer/company/user) bez dupliciranih poziva za istu vrijednost.
function collectUniqueValues(logs: ReservationChangeLogModel[], fieldNames: string[]): string[] {
    const values = new Set<string>();
    logs
        .filter((row) => fieldNames.includes(row.fieldName))
        .forEach((row) => {
            if (row.oldValue) values.add(row.oldValue);
            if (row.newValue) values.add(row.newValue);
        });
    return Array.from(values);
}

function ReservationHistory(props: ReservationHistoryProps) {
    const { reservation } = props;
    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState<ReservationChangeLogModel[]>([]);
    const [customerNames, setCustomerNames] = useState<Record<string, string>>({});
    const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
    const [userNames, setUserNames] = useState<Record<string, string>>({});
    const [reservationLabels, setReservationLabels] = useState<Record<string, string>>({});

    useEffect(() => {
        // U grupnoj rezervaciji prebacivanje sobe dok je Povijest tab aktivan NE unmounta ovu
        // komponentu (tabsBody.tsx samo mijenja reservation prop) - zato mora u dependency listi
        // biti reservation?.id, inace se stari logovi ostave prikazani za novu sobu. "cancelled"
        // flag sprjecava i race condition ako se sobe prebacuju brze nego sto stigne fetch odgovor
        // (stariji poziv, cim vidi da je otkazan, ne smije prepisati stanje novijim/tudjim podacima).
        let cancelled = false;
        loadData(() => cancelled);
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line
    }, [reservation?.id]);

    const loadData = async (isCancelled: () => boolean) => {
        try {
            setLoading(true);
            if (reservation?.id) {
                const result = await getReservationChangeLog(reservation.id);
                if (isCancelled()) {
                    return;
                }
                setLogs(result || []);
                await Promise.all([
                    loadCustomerNames(result || []),
                    loadCompanyNames(result || []),
                    loadUserNames(result || []),
                    loadReservationLabels(result || []),
                ]);
            }
        } finally {
            if (!isCancelled()) {
                setLoading(false);
            }
        }
    };

    const loadCustomerNames = async (result: ReservationChangeLogModel[]) => {
        const ids = collectUniqueValues(result, ['customerId']);
        if (ids.length === 0) {
            return;
        }
        const entries = await Promise.all(
            ids.map(async (id) => {
                try {
                    const customer = await getCustomer(Number(id));
                    const name = `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim();
                    return [id, name || id] as [string, string];
                } catch {
                    return [id, id] as [string, string];
                }
            })
        );
        setCustomerNames(Object.fromEntries(entries));
    };

    const loadCompanyNames = async (result: ReservationChangeLogModel[]) => {
        const ids = collectUniqueValues(result, ['companyId']);
        if (ids.length === 0) {
            return;
        }
        const entries = await Promise.all(
            ids.map(async (id) => {
                try {
                    const company = await getCompany(Number(id));
                    return [id, company?.name || id] as [string, string];
                } catch {
                    return [id, id] as [string, string];
                }
            })
        );
        setCompanyNames(Object.fromEntries(entries));
    };

    const loadUserNames = async (result: ReservationChangeLogModel[]) => {
        const ids = collectUniqueValues(result, ['receptionistUuid', 'condoUserUuid']);
        if (ids.length === 0) {
            return;
        }
        const entries = await Promise.all(
            ids.map(async (uuid) => {
                try {
                    const user = await getUserInfo(uuid);
                    const name = `${(user as any)?.name || ''} ${(user as any)?.surname || ''}`.trim();
                    return [uuid, name || uuid] as [string, string];
                } catch {
                    return [uuid, uuid] as [string, string];
                }
            })
        );
        setUserNames(Object.fromEntries(entries));
    };

    // Invoice.reservationId (npr. "Spremi na rezervaciju"/"Ukloni") nije citljivo kao sirovi broj -
    // rezervacije nisu sve globalno ucitane u store-u (samo dolazece/prijavljene), pa se dohvacaju
    // on-demand i resolvaju na naziv sobe (isti resolveRoomName lookup kao za roomInfoId).
    const loadReservationLabels = async (result: ReservationChangeLogModel[]) => {
        const ids = collectUniqueValues(result, ['reservationId']);
        if (ids.length === 0) {
            return;
        }
        const entries = await Promise.all(
            ids.map(async (id) => {
                try {
                    const res = await getReservation(Number(id));
                    const roomName = res?.roomInfoId ? resolveRoomName(String(res.roomInfoId)) : null;
                    const label = roomName ? `${roomName} (#${id})` : `#${id}`;
                    return [id, label] as [string, string];
                } catch {
                    return [id, id] as [string, string];
                }
            })
        );
        setReservationLabels(Object.fromEntries(entries));
    };

    if (loading) {
        return (
            <div className="mt-4p d-flex justify-content-center">
                <MiniLoader />
            </div>
        );
    }

    const groups = groupByActionUuid(logs).slice().reverse();
    const entityIdToCustomerId = buildEntityIdToCustomerIdMap(logs);
    const displayRows = buildDisplayRows(
        groups,
        { customerNames, companyNames, userNames, reservationLabels },
        entityIdToCustomerId
    );

    return (
        <div className="mt-4p" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {displayRows.length === 0 ? (
                <div>{translate('resLog_NoChangesRecorded')}</div>
            ) : (
                <>
                    <div className="custom-list-header">
                        <div>{translate('Time')}</div>
                        <div />
                        <div>{translate('resLog_ChangedBy')}</div>
                    </div>
                    {displayRows.map((row) => (
                        <div key={row.key} id="no-hover" className="custom-list-item">
                            <div>{formatDateTime(row.recordedAt)}</div>
                            <div>
                                {row.label}: {row.oldDisplay} → {row.newDisplay}
                            </div>
                            <div>{row.actorLabel}</div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}

export default ReservationHistory;
